import React, { useEffect, useState } from 'react';
import { FiX, FiCheckCircle, FiShield, FiLock, FiTag, FiLoader } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import styles from '../../../style/payment/PaymentWindow.module.css'; 

const PaymentWindow = ({ 
  isOpen, 
  onClose,
  qrData, 
  amount, 
  upiLink,
  onConfirm,
  isSubmitting,
  onCouponApply
}) => {
  const [transactionId, setTransactionId] = useState('');
  const [error, setError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      return () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.body.classList.remove('modal-open');
      };
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!transactionId.trim()) {
      setError('Transaction ID is required');
      return;
    }
    onConfirm(transactionId);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage('Please enter a coupon code');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponMessage('');

    try {
      const success = await onCouponApply(couponCode);
      
      if (success) {
        setCouponMessage('Coupon applied successfully! New QR code generated');
      } else {
        setCouponMessage('Invalid coupon code');
      }
    } catch (error) {
      setCouponMessage('Failed to apply coupon. Please try again.');
      console.error('Coupon apply error:', error);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center p-4 sm:p-6 bg-black bg-opacity-60 custom-blur overflow-y-auto">
          <motion.div
            key="modal"
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-white rounded-xl shadow-3xl w-full max-w-4xl mx-auto my-16 flex flex-col"
            style={{ 
              maxHeight: 'calc(100vh - 8rem)',
              marginTop: '4rem'
            }}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Complete Payment</h2>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors p-1 sm:p-2 rounded-lg hover:bg-gray-100">
                <FiX className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Scrollable Content with custom scrollbar */}
            <div className="overflow-y-auto flex-1 custom-scroll">
              <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 md:gap-8 p-4 sm:p-6 md:p-8">
                {/* Left Section - QR Code and Transaction */}
                <div className="flex-1 space-y-4 sm:space-y-6">
                  {/* QR Code Section */}
                  <div className={styles.qrContainer}>
                    <motion.div
                      key={qrData}
                      initial={{ opacity: 0.5, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={styles.qrImageWrapper}
                      style={{ maxWidth: '100%', padding: '1rem' }}
                    >
                      {qrData ? (
                        <img 
                          src={qrData} 
                          alt="UPI QR Code" 
                          className={styles.qrImage}
                          style={{ 
                            width: '100%', 
                            height: 'auto',
                            maxWidth: '300px',
                            margin: '0 auto',
                            display: 'block'
                          }}
                          onError={(e) => {
                            e.target.src = '/fallback-qr-placeholder.png';
                            console.error('QR code failed to load');
                          }}
                        />
                      ) : (
                        <div className={styles.loadingState}>
                          <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                          <p className="mt-2">Generating QR code...</p>
                        </div>
                      )}
                    </motion.div>

                    <div className={styles.merchantInfo}>
                      <p className={styles.merchantName}>Himanshu Salary Corner</p>
                      <p className={styles.merchantSubtext}>Scan QR to pay via any UPI app</p>
                    </div>

                    <div className={`${styles.paymentApps} flex justify-center gap-3 sm:gap-4`}>
                      <img src="/Images/Gpay.png" alt="GPay" className="h-8 sm:h-10 w-auto" />
                      <img src="/Images/phonePe.png" alt="PhonePe" className="h-8 sm:h-10 w-auto" />
                      <img src="/Images/paytm.png" alt="Paytm" className="h-8 sm:h-10 w-auto" />
                    </div>
                  </div>

                  {/* Secure Payment Gateway Section */}
                  <div className="p-4 sm:p-6 rounded-lg border border-gray-200 bg-white shadow-sm">
                    <h3 className="text-lg font-semibold mb-3 sm:mb-4 text-gray-800 flex items-center gap-2">
                      <FiShield className="text-blue-600" />
                      Secure Payment Gateway
                    </h3>
                    <div className="space-y-2 sm:space-y-3 text-sm text-gray-600">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <FiCheckCircle className="flex-shrink-0 mt-0.5 sm:mt-1 text-green-600" />
                        <p>No sensitive payment data is stored on our servers</p>
                      </div>
                    </div>
                  </div>

                  {/* Transaction ID Input */}
                  <div className="space-y-2 sm:space-y-3 text-sm">
                    <div className="space-y-1 sm:space-y-2">
                      <label className="block text-gray-600 mb-1">Transaction ID *</label>
                      <input
                        type="text"
                        value={transactionId}
                        onChange={(e) => {
                          setTransactionId(e.target.value);
                          if (error) setError('');
                        }}
                        className={`w-full p-2 sm:p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          error ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter UPI transaction reference number"
                        required
                      />
                      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                    </div>
                  </div>
                </div>

                {/* Right Section - Payment Details */}
                <div className="flex-1 space-y-4 sm:space-y-6">
                  <div className="p-4 sm:p-6 rounded-lg border border-gray-200 bg-white shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 sm:mb-6 text-gray-800">Payment Details</h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div className="flex justify-between items-center pb-3 sm:pb-4 border-b border-gray-200">
                        <span className="text-gray-600">Amount Payable:</span>
                        <span className="text-2xl sm:text-3xl font-bold text-gray-900">₹{amount}</span>
                      </div>

                      {/* Coupon Code Section */}
                      <div className="space-y-2 sm:space-y-3">
                        <label htmlFor="coupon" className="block text-gray-600 flex items-center gap-2">
                          <FiTag className="text-blue-500" />
                          Apply Coupon Code
                        </label>
                        
                        <div className="flex gap-2">
                          <input
                            type="text"
                            id="coupon"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            className="flex-1 p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter coupon code"
                          />
                          <button
                            onClick={handleApplyCoupon}
                            disabled={isApplyingCoupon}
                            className={`px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium flex items-center gap-1 sm:gap-2 ${
                              isApplyingCoupon
                                ? 'bg-gray-200 text-gray-700'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                          >
                            {isApplyingCoupon ? (
                              <>
                                <FiLoader className="animate-spin" />
                                <span className="hidden sm:inline">Applying...</span>
                              </>
                            ) : 'Apply'}
                          </button>
                        </div>
                        
                        {couponMessage && (
                          <p className={`text-sm ${
                            couponMessage.includes('success') 
                              ? 'text-green-600' 
                              : 'text-red-500'
                          }`}
                          >
                            {couponMessage}
                          </p>
                        )}
                      </div>

                      {/* Payment Instructions */}
                      <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-100">
                        <h4 className="font-medium text-blue-800 mb-1 sm:mb-2 flex items-center gap-2">
                          <FiCheckCircle className="text-blue-600" />
                          Payment Instructions
                        </h4>
                        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 sm:space-y-2">
                          <li>Scan the QR code using any UPI app</li>
                          <li>Complete the payment in your UPI app</li>
                          <li>Enter the transaction ID received</li>
                          <li>Click 'Confirm Payment'</li>
                        </ol>
                      </div>

                      {/* Confirm Payment Button */}
                      <button
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className={`w-full flex items-center justify-center gap-2 ${
                          isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                        } text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg transition-all font-medium shadow-lg hover:shadow-xl`}
                      >
                        <FiCheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                        {isSubmitting ? 'Processing...' : 'Confirm Payment'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentWindow;